import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import type { DisplayDate } from 'owa-datetime';
import type { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import type { DroppableType } from 'owa-calendar-surface-dategrid';
import { lazyOnTodoItemDropped, lazyOnCalendarEventDropped } from 'owa-calendar-surface-dnd';
import { requestModuleCommand, resetModuleCommand } from 'owa-calendar-module-command';
import {
    canDragItem as canDragItemBase,
    canDropItemType as canDropItemTypeBase,
    getSurfaceItemForPreview as getSurfaceItemForPreviewBase,
    onDragStart as onDragStartBase,
    onDragEnd as onDragEndBase,
} from 'owa-calendar-surface-dategrid-common-dnd-callbacks';

/**
 * Whether the given item can be dragged
 * @param item The surface item model
 */
export function canDragItem(item: CalendarEvent): boolean {
    return canDragItemBase(item);
}

/**
 * Whether the specified itemtype can be dropped on the surface.
 * @param dragItemType The itemType of the item being dragged onto the surface.
 */
export function canDropItemType(dragItemType: string, droppableCalendarFolderId: string): boolean {
    return canDropItemTypeBase(dragItemType, droppableCalendarFolderId);
}

/**
 * Callback for getting the item to show on surface during a drop operation.
 * This is needed when dragging items that didn't start on the surface,
 * so we don't have an item to start with.
 * @param dragInfo The dragInfo of the item being dragged
 * @param dragStartDate The DisplayDate corresponding to the point where the dragged item
 * entered the calendar surface
 * @param droppableType DroppableType of the region of the calendar surface
 */
export function getSurfaceItemForPreview(
    dragInfo: DragData,
    initialDateTime: DisplayDate,
    droppableType: DroppableType
): CalendarEvent {
    return getSurfaceItemForPreviewBase(dragInfo, initialDateTime, droppableType);
}

/**
 * Called when the item is dropped in one of the droppable areas of the surface.
 * @param droppedItem The dropped item with modified time and all-day flag, if needed
 * @param originalItem The original item prior to updates
 * @param dragInfo The DragData for the drag-drop operation
 */
export function onItemDropped(
    droppedItem: CalendarEvent,
    originalItem: CalendarEvent,
    dragInfo: DragData
) {
    let itemType = dragInfo.itemType;

    // this is a temporary solution untill I refactor the Client-Controls Droppable and Draggable components to handle containerId in DragData
    // tracked by VSO #33820
    if (dragInfo.itemType.indexOf('_') > 0) {
        let splitStrings = dragInfo.itemType.split('_');
        itemType = splitStrings[0];
    }

    if (itemType == DraggableItemTypes.CalendarItem) {
        lazyOnCalendarEventDropped.importAndExecute(droppedItem, originalItem);
    } else if (itemType == DraggableItemTypes.Todo) {
        lazyOnTodoItemDropped.importAndExecute(droppedItem, dragInfo);
    }
}

/**
 * Called when the user starts dragging an item
 * @param item The item being dragged
 */
export function onDragStart(item: CalendarEvent) {
    requestModuleCommand('DragItem');
    onDragStartBase(item);
}

/**
 * Called when the user ends dragging an item by dropping it inside or outside a droppable area.
 * @param item The item being dragged
 */
export function onDragEnd(surfaceItem: CalendarEvent) {
    onDragEndBase(surfaceItem);
    resetModuleCommand('DragItem');
}
